/**
 * CLIENTE SUPABASE FRONTEND - APENAS AUTENTICAÇÃO
 * ================================================
 * Cliente minimalista para frontend com APENAS chave pública
 * Todas as operações de dados são feitas via API routes server-side
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!
const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY! // Apenas chave pública

// Logs de configuração removidos para segurança

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('❌ [SUPABASE-CLIENT] Variáveis de ambiente não configuradas');
  throw new Error('Variáveis públicas do Supabase não encontradas')
}

/**
 * Cliente Supabase para frontend - APENAS autenticação
 * Sem acesso direto às tabelas, tudo via API routes
 */
// Limpar localStorage antigo na primeira vez (migração para cookies)
if (typeof window !== 'undefined') {
  const oldKeys = Object.keys(localStorage).filter(key => key.startsWith('sb-'));
  if (oldKeys.length > 0) {
    oldKeys.forEach(key => localStorage.removeItem(key));
  }
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    // Usando cookies padrão do Supabase para permitir server actions lerem a sessão
  },
  global: {
    headers: {
      'x-application-name': 'innovagro-app'
    }
  },
  db: {
    schema: 'public'
  }
})

// Bootstrap: registra o provider de access token que o `apiFetch` de
// `@v1/http/client` usa pra anexar `Authorization: Bearer ...`. Vive aqui
// (v1) porque a Regra de Ouro proíbe v2 de importar o cliente Supabase
// (v1). Quando o frontend migrar pra v2, o provider muda de lugar.
import('@v1/http/client').then(({ setTokenProvider }) => {
  setTokenProvider(async () => {
    // Fonte primária: JWT do Better Auth (auth v2), que é como o app loga hoje.
    // Sem isto o provider só lia a sessão Supabase — vazia sob Better Auth — e
    // toda rota v1 atrás de `withAuth` respondia 401 (fila da Mesa/validação
    // "vazias"). O `verifyJwt` da v1 já aceita token Better Auth via JWKS.
    const betterAuthToken = lerAccessTokenBetterAuth()
    if (betterAuthToken) return betterAuthToken

    // Fallback legado: sessão Supabase, enquanto algum fluxo ainda a criar.
    const { data } = await supabase.auth.getSession()
    return data.session?.access_token ?? null
  })
}).catch(() => { /* v2 client opcional — silencia se o módulo não estiver disponível */ })

/**
 * Lê o access token (JWT) do Better Auth que o AuthManager (v2) persiste em
 * localStorage. A chave (`agros_auth`) e o formato (`{ accessJwt }`) são
 * REPLICADOS aqui — nunca importados de (v2) — pra respeitar a Regra de Ouro de
 * isolamento v1↔v2. Se a convenção da v2 mudar, ajustar este literal.
 */
function lerAccessTokenBetterAuth(): string | null {
  if (typeof window === 'undefined') return null
  const chave = process.env.NEXT_PUBLIC_AUTH_STORAGE_KEY ?? 'agros_auth'
  try {
    const raw = window.localStorage.getItem(chave)
    if (!raw) return null
    const cred = JSON.parse(raw) as { accessJwt?: string }
    return cred?.accessJwt ?? null
  } catch {
    return null
  }
}

/**
 * Utilitários de autenticação frontend
 */
export const authClient = {
  /**
   * Fazer login com email e senha
   */
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password
    })
    
    if (error) throw error
    return data
  },

  /**
   * Fazer logout
   */
  async signOut() {
    const { error } = await supabase.auth.signOut()
    if (error) throw error
  },

  /**
   * Obter usuário atual
   */
  async getUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    if (error) throw error
    return user
  },

  /**
   * Obter sessão atual
   */
  async getSession() {
    const { data: { session }, error } = await supabase.auth.getSession()
    if (error) throw error
    return session
  },

  /**
   * Escutar mudanças na autenticação
   */
  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback)
  }
}

/**
 * Cliente de API para operações de dados (via fetch para API routes)
 */
export const apiClient = {
  /**
   * Fazer requisição autenticada para API routes
   */
  async request(endpoint: string, options: RequestInit = {}) {
    const session = await authClient.getSession()
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    }
    
    // Adicionar token de autorização se disponível
    if (session?.access_token) {
      headers['Authorization'] = `Bearer ${session.access_token}`
    }
    
    const response = await fetch(`/api${endpoint}`, {
      ...options,
      headers,
    })
    
    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.error || 'Erro na requisição')
    }
    
    return response.json()
  },

  // Operações de comercializações
  comercializacoes: {
    async getAll() {
      return apiClient.request('/comercializacoes')
    },
    
    async getById(id: string) {
      return apiClient.request(`/comercializacoes/${id}`)
    },
    
    async create(data: any) {
      return apiClient.request('/comercializacoes', {
        method: 'POST',
        body: JSON.stringify(data)
      })
    },
    
    async update(id: string, data: any) {
      return apiClient.request(`/comercializacoes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      })
    },
    
    async delete(id: string) {
      return apiClient.request(`/comercializacoes/${id}`, {
        method: 'DELETE'
      })
    }
  },

  // Operações de assessorados
  assessorados: {
    async getAll() {
      return apiClient.request('/assessorados')
    },
    
    async getById(id: string) {
      return apiClient.request(`/assessorados/${id}`)
    },
    
    async create(data: any) {
      return apiClient.request('/assessorados', {
        method: 'POST',
        body: JSON.stringify(data)
      })
    },
    
    async update(id: string, data: any) {
      return apiClient.request(`/assessorados/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data)
      })
    },
    
    async delete(id: string) {
      return apiClient.request(`/assessorados/${id}`, {
        method: 'DELETE'
      })
    }
  },

  // Dashboard
  dashboard: {
    async getStats() {
      return apiClient.request('/dashboard')
    }
  }
}

